import * as React from 'react';
import { type LucideIcon } from 'lucide-react';

import { cn } from '../utils/utils';

/**
 * The input classes for a touch-first field with a leading icon.
 *
 * Two corrections over the bare `Input`, both measured:
 *
 * - **44px, not the primitive's 36px.** `h-9` is a dense-product-UI height.
 *   This is the size for a form someone meets before they have an account, and
 *   for anything touch-first.
 * - **`bg-transparent`.** On a card the primitive's `bg-background` fill
 *   measures 1.165:1 against the card in light and 1.081:1 in dark, so it is a
 *   light-mode-only effect, while the border carries the field at 4.46:1 and
 *   5.50:1. Expressed as an explicit `bg-transparent` and never by deleting the
 *   class: a text input with no background utility falls back to the UA `field`
 *   colour, which is opaque white in BOTH themes here because the app declares
 *   no `color-scheme`, so Preflight (which only zeroes buttons) does not save
 *   it.
 */
export const iconFieldInputClass =
  'h-11 bg-transparent pl-10 text-base md:text-sm';

/**
 * Positions a leading icon over a field.
 *
 * **The input is passed as a child rather than rendered here**, and that is
 * load-bearing rather than stylistic. Inside a `FormControl` - a Radix `Slot` -
 * whatever this component returned as its root would receive the slot's `id`
 * and `aria-describedby`. If that root were this positioning `<div>`, the
 * label's `htmlFor` would resolve to a div and the input would have no
 * accessible name at all. Keeping the input as a child lets the consumer wrap
 * the input, and only the input, in `FormControl`:
 *
 * ```tsx
 * <IconFieldFrame icon={Mail}>
 *   <FormControl>
 *     <Input className={iconFieldInputClass} {...field} />
 *   </FormControl>
 * </IconFieldFrame>
 * ```
 *
 * That bug shipped once and went unnoticed because every field carried a
 * placeholder, which is accepted as a last-resort accessible name - remove the
 * placeholder and axe fails immediately.
 *
 * The icon sits at `top-3.5` because (44 - 16) / 2 = 14px. Pairing `top-3` with
 * `h-9` left gaps of 12 above and 8 below, so every glyph on the old sign-in
 * screen was 2px below centre.
 */
export function IconFieldFrame({
  icon: Icon,
  children,
  className,
}: {
  icon: LucideIcon;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('relative', className)}>
      <Icon
        className='text-muted-foreground pointer-events-none absolute top-3.5 left-3 z-10 h-4 w-4'
        aria-hidden
      />
      {children}
    </div>
  );
}
